/**
 * Console-error capture (replicated from brospace).
 *
 * Monkey-patches `console.error` once at app startup into a small ring buffer
 * (last {@link MAX_ERRORS}) so the feedback widget can attach recent errors to
 * a GitLab issue. The original `console.error` is always still called, so dev
 * logging is unaffected.
 *
 * Initialised from main.tsx. Skipped under MODE === 'test' so it never
 * pollutes vitest output; tests that exercise it call `_resetForTesting()`.
 */
const MAX_ERRORS = 20;

export interface ConsoleErrorEntry {
  message: string;
  timestamp: string;
}

const errorBuffer: ConsoleErrorEntry[] = [];

let initialized = false;
let _testingOverride = false;
let _originalConsoleError: typeof console.error | null = null;

export function initErrorCapture(): void {
  if (initialized) return;
  if (import.meta.env.MODE === 'test' && !_testingOverride) return;
  initialized = true;

  const originalError = console.error.bind(console);
  _originalConsoleError = originalError;

  console.error = (...args: unknown[]): void => {
    const message = args
      .map((a) => {
        if (a instanceof Error) return a.stack || a.message;
        if (typeof a === 'object' && a !== null) {
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        }
        return String(a);
      })
      .join(' ');
    errorBuffer.push({ message, timestamp: new Date().toISOString() });
    if (errorBuffer.length > MAX_ERRORS) errorBuffer.shift();
    originalError(...args);
  };
}

export function getConsoleErrors(): ConsoleErrorEntry[] {
  return [...errorBuffer];
}

export function clearConsoleErrors(): void {
  errorBuffer.length = 0;
}

export function _resetForTesting(): void {
  if (_originalConsoleError) {
    console.error = _originalConsoleError;
    _originalConsoleError = null;
  }
  initialized = false;
  _testingOverride = true;
  errorBuffer.length = 0;
}
